import { SUPPLIER_ELECTION } from "../../lib/supplierElection";   // A2-558
import React from "react";
import { useTranslation } from "react-i18next";
import { Warning } from "@phosphor-icons/react";
import { effectiveDateWarning } from "./VatFormatChip";
import { DateInput } from "../DateInput";
import { ALL_COUNTRY_CHOICES, countryOptionLabel, withCurrent } from "../../lib/countries";

/**
 * v0.6 — VAT Registrations table (client-edit declutter item 5).
 *  - v0.6.8 — IOSS and OSS were split out into their own dedicated
 *    sections above (ClientEditForm sections 3 and 4). This table now
 *    only shows LOCAL per-country registrations — any OSS_UNION/
 *    OSS_NON_UNION/OSS/IOSS entries in company_vat_numbers are managed
 *    by those sections and hidden here (still the same underlying
 *    array, just a different index is skipped in the render — setReg/
 *    rmReg still address rows by their real array index).
 *  - Frequency column: editable (Monthly/Quarterly/Annually — v0.6.8) for
 *    every row here, bound to the top-level uk_vat_frequency (GB) /
 *    local_return_frequency[country_code] (everyone else) fields.
 *  - v0.6.12: France also gets a "due day" box, bound to
 *    local_return_deadline_day[country_code]. It appears for France ALONE and
 *    that is the point: the DGFiP assigns each taxpayer's CA3 deadline from
 *    legal form and SIREN, so the engine has no legal source for it and
 *    carries a flat figure the operator gave for their whole client base.
 *    Every other country's day comes from a statute, where an override could
 *    only record something wrong — so no box is offered, and the backend
 *    ignores one even if it is posted.
 */

// Countries whose filing date is NOT set by law, so a client's own notice is
// the only real source. Mirrors engine/filing_cadence.operator_supplied_
// countries(), which derives it from the rule citations.
const DEADLINE_DAY_COUNTRIES = ["FR"];

// v0.6.12 — HMRC VATAAS3300: "the business' annual accounting year end will
// be the month which it requested on the Form VAT600AA". The engine assumed
// December for every annual filer, so a March year-end client was offered a
// January-December period they do not file. A dropdown rather than a typed
// month: there are exactly twelve answers.
const YEAR_END_MONTHS = [
  [1, "January"], [2, "February"], [3, "March"], [4, "April"],
  [5, "May"], [6, "June"], [7, "July"], [8, "August"],
  [9, "September"], [10, "October"], [11, "November"], [12, "December"],
];

const FrequencyCell = ({ row, ukVatFrequency, setUkVatFrequency,
                         localReturnFrequency, setLocalFrequency,
                         localDeadlineDay, setLocalDeadlineDay,
                         ukAnnualYearEndMonth, setUkAnnualYearEndMonth,
                         ukStaggerGroup, setUkStaggerGroup }) => {
  const { t } = useTranslation();
  const cc = (row.country_code || "").toUpperCase();
  if (!cc) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const isGb = cc === "GB";
  const label = isGb ? "UK VAT" : cc === "IT" ? "IT LIPE" : cc;
  const value = isGb
    ? (ukVatFrequency || "QUARTERLY")
    : ((localReturnFrequency || {})[cc] || "QUARTERLY");
  const onChange = (v) => (isGb ? setUkVatFrequency(v) : setLocalFrequency(cc, v));
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-slate-500 dark:text-slate-400">{label} ·</span>
      <select
        data-testid={`client-form-freq-${cc}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs rounded"
      >
        <option value="QUARTERLY">{t("ui.admin_clients.form.vat_table.freq_quarterly", { defaultValue: "Quarterly" })}</option>
        <option value="MONTHLY">{t("ui.admin_clients.form.vat_table.freq_monthly", { defaultValue: "Monthly" })}</option>
        <option value="SEMI_ANNUALLY">{t("ui.admin_clients.form.vat_table.freq_half_yearly", { defaultValue: "Half-yearly" })}</option>
        <option value="ANNUALLY">{t("ui.admin_clients.form.vat_table.freq_annually", { defaultValue: "Annually" })}</option>
      </select>
      {isGb && value === "QUARTERLY" && setUkStaggerGroup && (
        <label className="flex items-center gap-1">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t("ui.admin_clients.form.vat_table.quarters_end", { defaultValue: "quarters end" })}
          </span>
          <select
            data-testid="client-form-uk-stagger"
            value={ukStaggerGroup || 1}
            onChange={(e) => setUkStaggerGroup(Number(e.target.value))}
            title={
              "The client's HMRC stagger group, shown on its VAT registration "
              + "certificate and VAT online account (VAT Notice 700 section 20.3). "
              + "It sets which quarters the client files and their deadlines."
            }
            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs rounded"
          >
            <option value={1}>{t("ui.admin_clients.form.vat_table.stagger_1", { defaultValue: "Mar · Jun · Sep · Dec (group 1)" })}</option>
            <option value={2}>{t("ui.admin_clients.form.vat_table.stagger_2", { defaultValue: "Jan · Apr · Jul · Oct (group 2)" })}</option>
            <option value={3}>{t("ui.admin_clients.form.vat_table.stagger_3", { defaultValue: "Feb · May · Aug · Nov (group 3)" })}</option>
          </select>
        </label>
      )}
      {isGb && value === "ANNUALLY" && (
        <label className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 dark:text-slate-400">
            {t("ui.admin_clients.form.vat_table.year_ends", { defaultValue: "year ends" })}
          </span>
          <select
            data-testid="client-form-uk-year-end"
            value={ukAnnualYearEndMonth || 12}
            onChange={(e) => setUkAnnualYearEndMonth(Number(e.target.value))}
            title={
              "The month this client's annual accounting year ends in. HMRC "
              + "assigns it from the month the business asked for on form "
              + "VAT600AA, so it is not always December. It sets both the "
              + "period offered and the filing deadline, which is two months "
              + "after the period ends."
            }
            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs rounded"
          >
            {YEAR_END_MONTHS.map(([m, name]) => (
              <option key={m} value={m}>{name}</option>
            ))}
          </select>
        </label>
      )}
      {DEADLINE_DAY_COUNTRIES.includes(cc) && (
        <label className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 dark:text-slate-400">
            {t("ui.admin_clients.form.vat_table.due_day", { defaultValue: "due day" })}
          </span>
          <input
            data-testid={`client-form-due-day-${cc}`}
            type="number"
            min="1"
            max="28"
            placeholder="19"
            value={(localDeadlineDay || {})[cc] ?? ""}
            onChange={(e) => setLocalDeadlineDay(cc, e.target.value)}
            title={
              "The day this client's own notice gives. France's filing date is "
              + "assigned per taxpayer by the DGFiP from legal form and SIREN, "
              + "so there is no published rule to read it from. Leave blank to "
              + "use the default of the 19th. 1-28 only, because 29-31 do not "
              + "exist in every month."
            }
            className="w-14 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-1.5 py-1 text-xs rounded"
          />
        </label>
      )}
    </div>
  );
};

export const ClientFormVatTable = ({
  rows, setReg, addReg, rmReg,
  ukVatFrequency, setUkVatFrequency, localReturnFrequency, setLocalFrequency,
  ukAnnualYearEndMonth, setUkAnnualYearEndMonth,
  ukStaggerGroup, setUkStaggerGroup,
  localDeadlineDay, setLocalDeadlineDay,
}) => {
  const { t } = useTranslation();
  const localRows = (rows || [])
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => (r.registration_type || "LOCAL").toUpperCase() === "LOCAL");
  return (
    <div className="space-y-3">
      <p className="text-[10px] text-slate-500 dark:text-slate-400">
        {t("ui.admin_clients.form.vat_table.intro", { defaultValue: "Per-country local VAT registrations only — IOSS and OSS are configured in their own sections above. Frequency (Monthly / Quarterly / Annually) is editable per row — jurisdictions vary, and it often depends on turnover too, so set whatever this client's actually been assigned." })}
      </p>
      <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-300">
            <tr className="text-[10px] tracking-normal">
              <th className="px-3 py-3 text-left w-28">{t("ui.admin_clients.form.vat_table.col_country", { defaultValue: "Country" })}</th>
              {/* A minimum width: the only column without a fixed one took what the
                  frequency extras (UK quarter ends, FR due day) left, and showed two
                  characters of each number (operator screenshot 2026-11-01). */}
              <th className="px-3 py-3 text-left min-w-[13rem]">{t("ui.admin_clients.form.vat_table.col_vat_number", { defaultValue: "VAT number" })}</th>
              <th className="px-3 py-3 text-left w-44">{t("ui.settings.fields.effective_from", { defaultValue: "Effective from" })}</th>
              <th className="px-3 py-3 text-left w-40">{t("ui.admin_clients.form.vat_table.col_frequency", { defaultValue: "Frequency" })}</th>
              <th
                className="px-3 py-3 text-left w-32"
                title={t("ui.admin_clients.form.vat_table.col_establishment_title", {
                  defaultValue: "Does this registration reflect genuine local establishment — real staff or premises here — or is it registration-only? Affects whether a same-country sale is treated as domestic or as a non-established-supplier reverse charge (Art 192a PVD).",
                })}
              >
                {t("ui.admin_clients.form.vat_table.col_establishment", { defaultValue: "Establishment" })}
              </th>
              <th className="px-3 py-3 text-right w-28"></th>
            </tr>
          </thead>
          <tbody>
            {localRows.map(({ r, i }) => {
              const warn = effectiveDateWarning(r.registration_date, t);
              return (
                <tr key={i} data-testid={`client-form-reg-${i}`}
                    className="border-t border-slate-200 dark:border-slate-700 align-middle">
                  <td className="px-3 py-3">
                    {/* v0.6.12 A5-27 — picked from the shared list, not
                        typed: a typed "UK" or "GE" was accepted here. */}
                    <select
                      data-testid={`client-form-reg-${i}-country`}
                      aria-label={t("ui.admin_clients.form.reg_country_aria", { defaultValue: "Registration country" })}
                      className="w-24 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-2 py-2 tabular-nums text-sm rounded"
                      value={(r.country_code || "").toUpperCase()}
                      onChange={(e) => setReg(i, "country_code", e.target.value)}
                    >
                      <option value="">—</option>
                      {withCurrent(ALL_COUNTRY_CHOICES, r.country_code).map((c) => (
                        <option key={c} value={c}>{countryOptionLabel(c)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <input
                      data-testid={`client-form-reg-${i}-number`}
                      className="w-full min-w-[12rem] bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 tabular-nums text-sm rounded"
                      value={r.vat_number || ""}
                      onChange={(e) => setReg(i, "vat_number", e.target.value)}
                      placeholder={t("ui.admin_clients.form.vat_table.col_vat_number", { defaultValue: "VAT number" })}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      {/* v0.6.12 A5-27 — the app's DD/MM/YYYY date box
                          with its calendar, not free "YYYY-MM-DD" text. It
                          still stores ISO. */}
                      <DateInput
                        testid={`client-form-reg-${i}-date`}
                        aria-label={t("ui.admin_clients.form.reg_date_aria", { defaultValue: "Date the registration took effect" })}
                        allowClear
                        className="w-36 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 tabular-nums text-sm rounded"
                        value={r.registration_date || ""}
                        onChange={(iso) => setReg(i, "registration_date", iso)}
                      />
                      {warn && (
                        <span
                          data-testid={`client-form-reg-${i}-date-warning`}
                          title={warn}
                          className="text-amber-600 dark:text-amber-400"
                        >
                          <Warning size={18} weight="fill" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <FrequencyCell
                      row={r}
                      ukVatFrequency={ukVatFrequency}
                      ukAnnualYearEndMonth={ukAnnualYearEndMonth}
                      ukStaggerGroup={ukStaggerGroup}
                      setUkStaggerGroup={setUkStaggerGroup}
                      setUkAnnualYearEndMonth={setUkAnnualYearEndMonth}
                      setUkVatFrequency={setUkVatFrequency}
                      localReturnFrequency={localReturnFrequency}
                      setLocalFrequency={setLocalFrequency}
                      localDeadlineDay={localDeadlineDay}
                      setLocalDeadlineDay={setLocalDeadlineDay}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <label
                      className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 cursor-pointer"
                      title={t("ui.admin_clients.form.vat_table.establishment_title", { defaultValue: "Genuine local establishment (real staff/premises), not just a VAT number. Leave unchecked for registration-only." })}
                    >
                      <input
                        type="checkbox"
                        data-testid={`client-form-reg-${i}-establishment`}
                        checked={!!r.local_establishment}
                        onChange={(e) => setReg(i, "local_establishment", e.target.checked)}
                        className="rounded border-slate-300 dark:border-slate-700"
                      />
                      {r.local_establishment
                        ? t("ui.admin_clients.form.vat_table.established", { defaultValue: "Established" })
                        : t("ui.admin_clients.form.vat_table.registration_only", { defaultValue: "Registration only" })}
                    </label>
                    {/* A2-558 — where the law lets a non-established supplier ask to
                        pay the VAT itself (SE ML 16 kap. 7 §). Off unless ticked:
                        registering is not that request. */}
                    {SUPPLIER_ELECTION[(r.country_code || "").toUpperCase()]
                      && (r.registration_type || "LOCAL").toUpperCase() === "LOCAL"
                      && !r.local_establishment && (
                      <label
                        className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 cursor-pointer"
                        title={SUPPLIER_ELECTION[(r.country_code || "").toUpperCase()]}
                      >
                        <input
                          type="checkbox"
                          data-testid={`client-form-reg-${i}-supplier-liable`}
                          checked={!!r.supplier_liable}
                          onChange={(e) => setReg(i, "supplier_liable", e.target.checked)}
                          className="rounded border-slate-300 dark:border-slate-700"
                        />
                        {t("ui.admin_clients.form.vat_table.supplier_liable", { defaultValue: "Asked to pay the VAT itself on sales to registered businesses" })}
                      </label>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      type="button"
                      data-testid={`client-form-reg-${i}-remove`}
                      onClick={() => {
                        // eslint-disable-next-line no-alert
                        if (typeof window !== "undefined"
                            && !window.confirm(
                              `Remove ${r.country_code || "?"} registration ${r.vat_number || ""}?\n\nThis removes the row from the client profile on save. Historical filings that already used this registration are not affected.`)) {
                          return;
                        }
                        rmReg(i);
                      }}
                      title={t("ui.admin_clients.form.vat_table.remove_title", { defaultValue: "Remove this registration" })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-normal text-rose-700 dark:text-rose-300 hover:text-rose-100 border border-rose-200 dark:border-rose-800 hover:border-rose-400 hover:bg-rose-700/10 rounded"
                    >
                      {t("ui.admin_clients.form.vat_table.remove", { defaultValue: "× Remove" })}
                    </button>
                  </td>
                </tr>
              );
            })}
            {localRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400 text-xs italic">
                  {t("ui.admin_clients.form.vat_table.empty", { defaultValue: "No local VAT registrations yet — add the first one below." })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        data-testid="client-form-add-reg"
        onClick={addReg}
        className="text-[11px] tracking-normal text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 border border-teal-200 dark:border-teal-800 hover:border-teal-500 dark:hover:border-teal-400 px-4 py-2 rounded"
      >
        {t("ui.admin_clients.form.vat_table.add", { defaultValue: "+ Add registration" })}
      </button>
    </div>
  );
};
